import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { FlexBox } from '../box';
import { Button, useIconButtonPopoverProps } from '../button';
import { CardActions, CardContent, CardHeader } from '../card';
import { Chip } from '../chip';
import { ColorIndicator } from '../color-indicator';
import { EmptyState } from '../empty-state';
import { ErrorState } from '../error-state';
import { useOverflow, useSize } from '../hooks';
import { Menu } from '../menu';
import { Paper } from '../paper';
import { Popover } from '../popover';
import { Skeleton } from '../skeleton';
import { styled } from '../styled';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { withVersion } from '../utils';
import { WidgetGrid } from './grid';
import { WidgetList } from './list';
export const WIDGET_MAX_WIDTH = 700;
export const WIDGET_HEIGHT = 460;
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint, @typescript-eslint/no-explicit-any
const WidgetImpl = (props) => {
    const content = (() => {
        if (props.error) {
            return _jsx(ErrorState, { children: props.error.message });
        }
        if (props.empty) {
            return _jsx(EmptyState, { ...props.empty });
        }
        const cloneProps = {
            footerAction: Boolean(props.footerAction || props.footerCaption),
            loading: Boolean(props.loading)
        };
        return React.cloneElement(props.children, cloneProps);
    })();
    const contentSx = () => {
        const css = {
            display: 'flex',
            overflow: 'hidden',
            flexBasis: '100%',
            p: 0,
            ':last-child': {
                pb: 0
            }
        };
        if (props.empty || props.error) {
            css.alignItems = 'center';
            css.py = 3;
            css[':last-child'] = { pb: 3 };
        }
        return css;
    };
    return (_jsxs(Paper, { elevation: "S", sx: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: props.maxWidth ?? WIDGET_MAX_WIDTH,
            height: props.height === 'fit-content' ? undefined : WIDGET_HEIGHT,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: (theme) => theme.palette.elevation?.outlined,
            overflow: props.height === 'fit-content' ? undefined : 'hidden'
        }, children: [_jsx(CardHeader, { sx: {
                    px: 2,
                    pt: 2,
                    pb: 1
                }, slotProps: {
                    action: { sx: { mt: 0, mr: 0, mb: 0 } },
                    content: { sx: { minWidth: 0, overflow: 'hidden' } }
                }, title: _jsxs(FlexBox, { gap: 2, stretch: true, vAlign: "center", children: [props.color && !props.loading && (_jsx(ColorIndicator, { color: props.color })), _jsx(WidgetTitle, { loading: props.loading, children: props.title }), (props.chip || props.facilityBadge) && (_jsxs(FlexBox, { stretch: true, gap: 1, hAlign: "flex-end", children: [props.chip && !props.loading && (_jsx(Chip, { ...props.chip })), props.facilityBadge &&
                                    !props.loading &&
                                    props.facilityBadge] }))] }), action: _jsxs(FlexBox, { vAlign: "center", children: [props.info && (_jsx(HeaderInfo, { children: _jsx(FlexBox, { width: 318, p: 2, children: props.info }) })), props.actions && (_jsx(Menu, { item: props.item, actions: props.actions, icon: "IconMoreVert", iconProps: { fontSize: 'small' } }))] }) }), _jsx(CardContent, { sx: contentSx, children: content }), (props.footerAction || props.footerCaption) && (_jsxs(FooterCardActions, { children: [props.footerCaption && (_jsx(Typography, { variant: "detail", color: "text.secondary", children: props.footerCaption })), props.footerAction && (_jsx(Button, { fullWidth: true, size: "large", skeleton: props.loading, ...props.footerAction }))] }))] }));
};
WidgetImpl.List = WidgetList;
WidgetImpl.Grid = WidgetGrid;
export const Widget = withVersion(WidgetImpl, '1.0.0');
const FooterCardActions = styled(CardActions)({
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 4,
    paddingBottom: 16
});
const WidgetTitle = (props) => {
    const { ref } = useSize();
    const { textOverflow } = useOverflow(ref, props.children);
    return (_jsx(Tooltip, { bypass: !textOverflow, title: props.children, children: _jsx(Typography, { ref: ref, variant: "headingSmall", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", children: props.loading && !props.children ? (_jsx(Skeleton, { width: 250, height: "100%" })) : (props.children) }) }));
};
const HeaderInfo = (props) => {
    const popoverProps = useIconButtonPopoverProps({
        size: 'small',
        icon: 'IconInfo',
        behaviour: 'hover'
    });
    return props.children ? _jsx(Popover, { ...props, ...popoverProps }) : null;
};
